import type { BudgetTransaction } from "types/BudgetTransaction";
import type { GroupSummary } from "types/Group";
import type { GroupTransaction } from "types/GroupTransaction";
import type { ImportantMarketMove } from "features/market";
import type { PositionInstrumentEvent } from "features/market";
import { toTimestamp } from "utils/dateUtils";

const DAY_MS = 24 * 60 * 60 * 1000;

export interface PersonalUpcomingPaymentRankingConfig {
    lookAheadDays: number;
    overdueWindowDays: number;
    maxShown: number;
    proximityWeight: number;
    amountWeight: number;
    amountReference: number;
}

export const PERSONAL_UPCOMING_PAYMENT_RANKING: PersonalUpcomingPaymentRankingConfig = {
    lookAheadDays: 30,
    overdueWindowDays: 30,
    maxShown: 1,
    proximityWeight: 0.68,
    amountWeight: 0.32,
    amountReference: 1_000_000,
};

export type PersonalUpcomingPaymentRanking = Partial<PersonalUpcomingPaymentRankingConfig>;

/**
 * Proximity + log-scaled amount (same formula as legacy rankUpcomingPayment in notifications.ts).
 */
export function rankPersonalPayment(
    transaction: BudgetTransaction,
    dueDays: number,
    config: PersonalUpcomingPaymentRankingConfig,
): number {
    const horizonDays = Math.max(1, dueDays < 0 ? config.overdueWindowDays : config.lookAheadDays);
    const distanceDays = Math.abs(dueDays);
    const safeDistanceDays = Math.max(0, Math.min(horizonDays, distanceDays));
    const proximityScore = 1 - safeDistanceDays / horizonDays;

    const amount = Math.max(0, Math.abs(Number(transaction.amount) || 0));
    const reference = Math.max(1, config.amountReference);
    const amountScore = Math.min(1, Math.log1p(amount) / Math.log1p(reference));

    return (proximityScore * config.proximityWeight) + (amountScore * config.amountWeight);
}

export interface GroupActivityRankingContext {
    /**
     * Member "id" used in splitDetails / payer (participant id in group).
     */
    currentParticipantId: string;
    now?: Date;
    /**
     * Only transactions within this many days get a positive newness score.
     */
    windowDays?: number;
}

/**
 * Higher for newer transactions that materially involve the current participant.
 */
export function rankGroupActivity(
    tx: GroupTransaction,
    group: GroupSummary,
    ctx: GroupActivityRankingContext,
): number {
    const now = ctx.now ?? new Date();
    const windowDays = ctx.windowDays ?? 14;
    const ts = toTimestamp(tx.date);
    if (!ts) return 0;
    const ageMs = now.getTime() - ts;
    const ageDays = Math.max(0, ageMs / DAY_MS);
    if (ageDays > windowDays) return 0;
    const newnessScore = 1 - Math.min(1, ageDays / windowDays);

    const pid = ctx.currentParticipantId;
    let involvement = 0;

    if (tx.type === "settlement") {
        const creditor = Object.keys(tx.splitDetails ?? {})[0];
        if (tx.payer === pid || creditor === pid) involvement = 1;
    } else {
        const userShare = Number(tx.splitDetails?.[pid] ?? 0);
        if (tx.payer === pid || userShare > 0) involvement = 1;
    }

    return newnessScore * 0.55 + involvement * 0.45;
}

/**
 * Same ordering priority as getActionableInstrumentEvents sort: overdue, dueToday, upcoming, then abs(daysUntil).
 */
export function rankInstrumentEvent(item: PositionInstrumentEvent): number {
    const statusBase = item.status === "overdue" ? 1_000_000
        : item.status === "dueToday" ? 500_000
            : 0;
    const proximity = Math.max(0, 10_000 - Math.abs(item.daysUntil));
    return statusBase + proximity;
}

export function rankQuoteMove(move: ImportantMarketMove): number {
    const volumeScore = move.volume != null && move.volume > 0 ? Math.log10(move.volume) : 0;
    const watchlistBoost = move.isWatchlistSymbol ? 100 : 0;
    return watchlistBoost + Math.abs(move.variation) * 10 + volumeScore;
}
